import type { TQueryConstructor } from '@hozana/api/types'
import { normalize } from '@hozana/redux/normalize'

import entities from 'config/entities'
import type { TRawCommunity } from 'config/entities'

import { setRetreatStatusToCommunities, setRetreatStatusToCommunity } from 'modules/community/functions'
import type { TLocalization } from 'modules/community/pages/CommunityPage/GoogleMapSection/GMap'

type TCommunityIdOnlyProp = { communityId: number }

export const getCommunityQuery: TQueryConstructor<TCommunityIdOnlyProp & { withRawDescription?: boolean }, number> = ({
  communityId,
  withRawDescription,
}) => ({
  queryKey: `getCommunity/${communityId}${withRawDescription ? '/withRawDescription' : ''}`,
  url: `/community/${communityId}`,
  params: { fields: ['sharesCount', 'communitiesPropositions'], ...(withRawDescription && { flavour: 'full' }) },
  normalize: (data: TRawCommunity) => {
    // set retreat status of community (getRetreatStatus) before adding to store
    const newCommunity = setRetreatStatusToCommunity(data)
    if (data.communitiesPropositions && data.communitiesPropositions.length > 0) {
      newCommunity.communitiesPropositions = setRetreatStatusToCommunities(data.communitiesPropositions)
    }
    return normalize(newCommunity, entities.community)
  },
})

export const getCommunityMembersLocationQuery: TQueryConstructor<TCommunityIdOnlyProp, TLocalization[]> = ({
  communityId,
}) => ({
  queryKey: `getCommunityMembersLocation/${communityId}`,
  url: `/community/${communityId}/members/location`,
  params: { fields: ['sharesCount'] },
  headers: { 'Cache-Control': 'public' },
})

export const getCommunityMembersQuery: TQueryConstructor<TCommunityIdOnlyProp, number[]> = ({ communityId }) => ({
  queryKey: `getCommunity/${communityId}/members`,
  url: `/community/${communityId}/members`,
  meta: {
    limit: 20,
  },
  normalize: (data: TRawCommunity[]) => normalize(data, [entities.user]),
})

export const getCommunitiesQuery: TQueryConstructor<
  {
    page?: number
    limit: number
    displayAll?: true
    order?: string
    ignoreWizard?: boolean
    filter?: string
    filterParam?: string
  },
  number[]
> = ({ page = 1, limit = 6, displayAll, order, ignoreWizard, filter, filterParam }) => ({
  queryKey: 'getCommunities' + (filter ? `/${filter}/${filterParam || ''}` : '') + (limit ? `/${limit}` : ''),
  url: `/communities`,
  params: {
    fields: ['lastPublication', 'hasJoined'],
    meta: ['hasMore', 'globalCount'],
    order,
    filter,
    filterParam,
    displayAll,
    ...(ignoreWizard && { ignoreWizard }),
  },
  meta: {
    limit,
    offset: (page - 1) * limit,
  },
  normalize: (data: TRawCommunity[]) => {
    // set retreat status of community (getRetreatStatus) before adding to store
    const newCommunities = setRetreatStatusToCommunities(data)
    return normalize(newCommunities, [entities.community])
  },
})

export const getCommunitiesListQuery: TQueryConstructor<void, number[]> = () => ({
  queryKey: 'getCommunitiesList',
  url: '/communities',
  params: {
    fields: ['lastPublication'],
    order: 'publications',
    filter: 'indexed',
    limit: 500,
  },
  normalize: (data: TRawCommunity[]) => {
    // set retreat status of community (getRetreatStatus) before adding to store
    const newCommunities = setRetreatStatusToCommunities(data)
    return normalize(newCommunities, [entities.community])
  },
})

export const incrementCommunityViewsCountMutation: TQueryConstructor<TCommunityIdOnlyProp, void> = ({
  communityId,
}) => ({
  queryKey: `incrementCommunityViewCount/${communityId}`,
  url: `/community/${communityId}/view`,
  method: 'POST',
})
